import { Helmet } from '@modern-js/runtime/head';

import { useLoaderData } from '@/shared/modern-router';

import { ErrorPage } from '@/shared/components/error-page/error-page';
import { NoSSR } from '@/shared/components/no-ssr';
import { extractTextFromHtml } from '@/shared/utils/extract-text-from-html.ssr';
import { ApplicationFormStatus } from '../shared/application-form-status';
import { CareerSiteDetails } from './components/career-site';
import { CareerSiteSkeleton } from './components/career-site.skeleton';
import { useAddResourceBundle } from './i18n/use-add-resource-bundle';
import { useTranslationPublicCareerSite } from './i18n/use-translations';
import type { TPageLoaderData } from './types';

export const CareerSitePageSSR = () => {
  useAddResourceBundle();
  const loaderData = useLoaderData() as TPageLoaderData;
  const { t } = useTranslationPublicCareerSite();

  // Handle not-found error
  if ('error' in loaderData && loaderData.error === 'not-found') {
    return (
      <div className="tw-h-[100vh]">
        <ApplicationFormStatus
          title={t('CAREER_SITE_STATUS_TITLE_NOT_FOUND')}
          description={t('CAREER_SITE_STATUS_DESCRIPTION_NOT_FOUND')}
          type="not-found"
        />
      </div>
    );
  }

  // Handle server-error
  if ('error' in loaderData && loaderData.error === 'server-error') {
    return (
      <div className="tw-h-[100vh]">
        <ErrorPage
          title={t('ERROR_PAGE_TITLE_CAREER_SITE')}
          description={t('ERROR_PAGE_DESCRIPTION_CAREER_SITE')}
          buttonText={t('RETRY')}
          refreshAction={() => window.location.reload()}
        />
      </div>
    );
  }

  // Handle missing careerSiteConfig (shouldn't happen, but defensive)
  if (!('careerSiteConfig' in loaderData) || !loaderData.careerSiteConfig) {
    return (
      <div className="tw-h-[100vh]">
        <ErrorPage
          title={t('ERROR_PAGE_TITLE_CAREER_SITE')}
          description={t('ERROR_PAGE_DESCRIPTION_CAREER_SITE')}
          buttonText={t('RETRY')}
          refreshAction={() => window.location.reload()}
        />
      </div>
    );
  }

  // Handle hidden career site
  if (loaderData.careerSiteConfig.is_hidden) {
    return (
      <div className="tw-h-[100vh]">
        <ApplicationFormStatus
          title={t('CAREER_SITE_STATUS_TITLE_NOT_FOUND')}
          description={t('CAREER_SITE_STATUS_DESCRIPTION_NOT_FOUND')}
          type="not-found"
        />
      </div>
    );
  }

  // Success path
  const { careerSiteConfig } = loaderData;

  // Determine domain based on environment for SSR
  const isProd = process.env.APP_ENV === 'production';
  const domain = isProd ? 'https://www.crehana.com' : 'https://qa.creha.co';

  // Get current path - use window.location.pathname on client, fallback to '/jobs' on server
  const currentPath =
    typeof window !== 'undefined' ? window.location.pathname : '/jobs';
  const url = `${domain}${currentPath}`;

  const title = `${careerSiteConfig?.title} - ${careerSiteConfig?.company_info?.name}`;
  const description = extractTextFromHtml(careerSiteConfig?.description || '');
  const logo = careerSiteConfig?.company_info?.logo || '';

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={logo} />
        <meta property="og:url" content={url} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={logo} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <NoSSR fallback={<CareerSiteSkeleton />}>
        <CareerSiteDetails careerSiteConfig={careerSiteConfig} />
      </NoSSR>
    </>
  );
};
